import { AfterViewInit, Directive, ElementRef, HostBinding, Input, OnDestroy, Renderer2 } from '@angular/core';

@Directive()
export abstract class BaseExpandableWidgetComponent implements AfterViewInit, OnDestroy {
  private static readonly hostInstanceMap = new WeakMap<HTMLElement, BaseExpandableWidgetComponent>();

  @Input()
  set enableExpansion(value: boolean | string | null | undefined) {
    const shouldEnable = this.coerceBooleanProperty(value);

    if (shouldEnable === this._enableExpansion) {
      return;
    }

    this._enableExpansion = shouldEnable;

    if (!this._enableExpansion && this._isExpanded) {
      this.setExpandedState(false, false);
    }

    if (this.viewInitialized) {
      this.updateExpansionControls();
    }
  }

  get enableExpansion(): boolean {
    return this._enableExpansion;
  }

  @HostBinding('class.widget-expandable')
  get hostExpandableClass(): boolean {
    return this.enableExpansion;
  }

  @HostBinding('class.widget-expanded')
  get hostExpandedClass(): boolean {
    return this.enableExpansion && this.isExpanded;
  }

  @HostBinding('attr.data-widget-expandable')
  get hostExpandableAttr(): string | null {
    return this.enableExpansion ? 'true' : null;
  }

  @HostBinding('attr.data-widget-expanded')
  get hostExpandedAttr(): string | null {
    return this.enableExpansion && this.isExpanded ? 'true' : null;
  }

  protected get hostElement(): HTMLElement {
    return this.elementRef.nativeElement;
  }

  protected get isExpanded(): boolean {
    return this._isExpanded;
  }

  private expandToggleButton: HTMLButtonElement | null = null;
  private expandToggleContainer: HTMLElement | null = null;
  private expandToggleClickUnlisten: (() => void) | null = null;
  private viewInitialized = false;
  private _enableExpansion = false;
  private _isExpanded = false;

  constructor(protected readonly elementRef: ElementRef<HTMLElement>, protected readonly renderer: Renderer2) {
    BaseExpandableWidgetComponent.hostInstanceMap.set(this.hostElement, this);
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    this.updateExpansionControls();
    if (this._enableExpansion) {
      setTimeout(() => this.onExpandedStateChanged(this._isExpanded));
    }
  }

  ngOnDestroy(): void {
    this.destroyExpansionControls();
    BaseExpandableWidgetComponent.hostInstanceMap.delete(this.hostElement);
  }

  protected toggleExpanded(): void {
    if (!this.enableExpansion) {
      return;
    }

    const shouldExpand = !this._isExpanded;

    if (shouldExpand) {
      this.collapseOtherWidgetsInSameContainer();
    }

    this.setExpandedState(shouldExpand);
  }

  protected updateExpansionControls(): void {
    if (!this.viewInitialized) {
      return;
    }

    if (!this.shouldDisplayExpandToggle()) {
      this.destroyExpansionControls();
      return;
    }

    this.ensureExpandToggleButton();
    this.relocateExpandToggleButtonIfNeeded(true);
    this.updateToggleButtonState();
    this.applyExpansionStyles();
  }

  protected setExpandedState(expanded: boolean, dispatchResize: boolean = true): void {
    const nextState = this.enableExpansion ? expanded : false;

    if (this._isExpanded === nextState) {
      if (dispatchResize) {
        this.dispatchResizeEvent();
      }
      return;
    }

    this._isExpanded = nextState;

    this.onExpandedStateChanged(this._isExpanded);
    this.updateToggleButtonState();
    this.applyExpansionStyles();

    if (dispatchResize) {
      this.dispatchResizeEvent();
    }

    this.scrollCardIntoView();
  }

  protected applyExpansionStyles(): void {
    const host = this.hostElement;
    const wrapper = this.getWidgetWrapper();

    if (!this.enableExpansion || !this.viewInitialized) {
      this.renderer.removeStyle(host, 'grid-row-end');
      this.renderer.removeStyle(host, '--widget-row-height');
      this.renderer.removeStyle(host, 'grid-column');
      if (wrapper) {
        this.renderer.removeStyle(wrapper, '--widget-row-height');
      }
      return;
    }

    if (this._isExpanded) {
      this.renderer.setStyle(host, 'grid-row-end', 'span var(--widget-expanded-row-span, 2)');
      this.renderer.setStyle(host, '--widget-row-height', 'var(--widget-expanded-row-height, 1150px)');
      this.renderer.setStyle(host, 'grid-column', 'auto / span var(--widget-expanded-column-span, 2)');
      if (wrapper) {
        this.renderer.setStyle(wrapper, '--widget-row-height', 'var(--widget-expanded-row-height, 1150px)');
      }
    } else {
      this.renderer.removeStyle(host, 'grid-row-end');
      this.renderer.removeStyle(host, '--widget-row-height');
      this.renderer.removeStyle(host, 'grid-column');
      if (wrapper) {
        this.renderer.removeStyle(wrapper, '--widget-row-height');
      }
    }
  }

  /**
   * React when the widget's data availability changes so expansion UI matches its state.
   * @param hasData The new data availability state.
   */
  protected onWidgetDataAvailabilityChanged(hasData: boolean): void {
    if (!hasData && this.viewInitialized && this._isExpanded) {
      this.setExpandedState(false);
    }

    this.updateExpansionControls();
  }

  /**
   * Determine if the expand toggle should be displayed.
   * @returns boolean indicating if the expand toggle should be shown
   */
  private shouldDisplayExpandToggle(): boolean {
    if (!this.enableExpansion) {
      return false;
    }

    return this.widgetHasDataForExpansion();
  }

  /**
   * Ensure widgets that surface a hasData flag suppress expansion until data exists.
   */
  private widgetHasDataForExpansion(): boolean {
    const instance = this as any;

    if ('hasData' in instance) {
      return !!instance.hasData;
    }

    return true;
  }

  protected getWidgetWrapper(): HTMLElement | null {
    return this.hostElement.querySelector('app-widget-card-wrapper') as HTMLElement | null;
  }

  protected collapseOtherWidgetsInSameContainer(): void {
    const host = this.hostElement;
    const dashboard = host.closest('.dashboardPage') as HTMLElement | null;

    if (!dashboard) {
      return;
    }

    const expandedElements = Array.from(
      dashboard.querySelectorAll('[data-widget-expandable="true"][data-widget-expanded="true"]')
    ) as HTMLElement[];

    expandedElements.forEach((element) => {
      if (element === host) {
        return;
      }

      const instance = BaseExpandableWidgetComponent.hostInstanceMap.get(element);
      if (instance) {
        instance.setExpandedState(false);
      }
    });
  }

  protected scrollCardIntoView(): void {
    const host = this.hostElement;

    setTimeout(() => {
      try {
        host.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch {
        host.scrollIntoView();
      }
    }, 80);
  }

  protected dispatchResizeEvent(): void {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('resize'));
    }

    try {
      const resizeEvent = new CustomEvent('thestream-card-resized', { bubbles: true });
      this.hostElement.dispatchEvent(resizeEvent);
    } catch {
      // ignore if CustomEvent is unavailable
    }
  }

  protected onExpandedStateChanged(_expanded: boolean): void {
    // intentionally blank; subclasses can override to react to expansion changes
  }

  private ensureExpandToggleButton(): void {
    if (this.expandToggleButton) {
      return;
    }

    const host = this.hostElement;
    const wrapper = this.getWidgetWrapper();
    const cardBody = wrapper?.querySelector('.card-body') as HTMLElement | null;
    const container = cardBody ?? wrapper ?? host;

    const button = this.renderer.createElement('button') as HTMLButtonElement;
    this.renderer.addClass(button, 'widget-expand-toggle');
    this.renderer.setAttribute(button, 'type', 'button');

    if (container === host || container === wrapper) {
      this.renderer.setStyle(container, 'position', 'relative');
    }

    if (container.firstChild) {
      this.renderer.insertBefore(container, button, container.firstChild);
    } else {
      this.renderer.appendChild(container, button);
    }

    this.expandToggleButton = button;
    this.expandToggleContainer = container;

    this.expandToggleClickUnlisten = this.renderer.listen(button, 'click', (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      this.toggleExpanded();
    });
  }

  private relocateExpandToggleButtonIfNeeded(retry: boolean = false, attempt: number = 0): void {
    if (!this.expandToggleButton) {
      return;
    }

    const host = this.hostElement;
    const wrapper = this.getWidgetWrapper();
    const cardBody = wrapper?.querySelector('.card-body') as HTMLElement | null;

    if (!cardBody) {
      if (retry && attempt < 5) {
        setTimeout(() => this.relocateExpandToggleButtonIfNeeded(true, attempt + 1), 100);
      }
      return;
    }

    if (this.expandToggleContainer === cardBody) {
      return;
    }

    if (this.expandToggleContainer && this.expandToggleContainer !== host) {
      this.renderer.removeStyle(this.expandToggleContainer, 'position');
    }

    if (cardBody.firstChild) {
      this.renderer.insertBefore(cardBody, this.expandToggleButton, cardBody.firstChild);
    } else {
      this.renderer.appendChild(cardBody, this.expandToggleButton);
    }

    this.expandToggleContainer = cardBody;
  }

  private destroyExpansionControls(): void {
    if (this.expandToggleClickUnlisten) {
      this.expandToggleClickUnlisten();
      this.expandToggleClickUnlisten = null;
    }

    if (this.expandToggleButton && this.expandToggleContainer) {
      try {
        this.renderer.removeChild(this.expandToggleContainer, this.expandToggleButton);
      } catch {
        // ignore if already removed
      }
    }

    const previousContainer = this.expandToggleContainer;

    this.expandToggleButton = null;
    this.expandToggleContainer = null;

    if (previousContainer && previousContainer !== this.hostElement) {
      this.renderer.removeStyle(previousContainer, 'position');
    }

    const wrapper = this.getWidgetWrapper();

    this.renderer.removeStyle(this.hostElement, 'grid-row-end');
    this.renderer.removeStyle(this.hostElement, '--widget-row-height');
    this.renderer.removeStyle(this.hostElement, 'position');
    this.renderer.removeStyle(this.hostElement, 'grid-column');
    if (wrapper) {
      this.renderer.removeStyle(wrapper, '--widget-row-height');
      this.renderer.removeStyle(wrapper, 'position');
    }
  }

  private updateToggleButtonState(): void {
    if (!this.expandToggleButton) {
      return;
    }

    const label = this._isExpanded ? 'Collapse' : 'Expand';
    this.renderer.setProperty(this.expandToggleButton, 'textContent', label);
    this.renderer.setAttribute(this.expandToggleButton, 'aria-pressed', this._isExpanded ? 'true' : 'false');
    this.renderer.setAttribute(this.expandToggleButton, 'aria-expanded', this._isExpanded ? 'true' : 'false');
    this.renderer.setAttribute(
      this.expandToggleButton,
      'aria-label',
      this._isExpanded ? 'Collapse widget' : 'Expand widget'
    );
    this.renderer.setAttribute(
      this.expandToggleButton,
      'title',
      this._isExpanded ? 'Collapse widget' : 'Expand widget'
    );
  }

  private coerceBooleanProperty(value: boolean | string | null | undefined): boolean {
    if (value === null || value === undefined) {
      return false;
    }

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();

      if (
        normalized === '' ||
        normalized === 'true' ||
        normalized === '1' ||
        normalized === 'yes' ||
        normalized === 'on'
      ) {
        return true;
      }

      if (normalized === 'false' || normalized === '0' || normalized === 'no' || normalized === 'off') {
        return false;
      }

      return true;
    }

    return value === true;
  }
}
